import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import { Link } from 'react-router-dom'
import ScrollIntoView from 'react-scroll-into-view'
import { intersection, debounce, uniq, clone } from 'lodash'
import Type from '../../Components/Type'
import Checkbox from '../../Components/Checkbox'
import L from '../../Components/L'
import { StyledMenu, StyledMenuItem } from '../../Components/NavMenu'
import { Sort } from '../MXCollectionPage/MXCollectionPage'
import {
  WEBSITE_CONFIG_COLLECTION,
  WEBSITE_CONFIG_ID,
  COLLECTIONS_COLLECTION,
  SIMULATIONS_COLLECTION,
  PRODUCTION,
} from '../../Config'
import Layout from '../../Components/Layout'
import SimulationBanner from '../../Components/SimulationBanner'
import IconArrowLeftDark from '../../Icons/IconArrowLeftDark'
import IconFilter from '../../Icons/IconFilter'
import AccountActions from '../../Redux/AccountRedux'
import LoginSSO from '../LoginSSO'
import { SimulationGrid, ShadowBox, StyledMenuItemInner, CollectionsStyles } from './Collections-styles'

export const FILTER_OPTIONS = {
  autorun: 'AutoRun Interactions',
  horizontal: 'Horizontal Content',
  simulator: 'Mobile Simulator',
  'mobile-app': 'Mobile App',
}

const Filter = ({ filters = [], options = [], availableOptions = [], onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [innerFilters, setInnerFilters] = useState(filters)
  const dropdownWidth = 0

  const handleClick = e => {
    document.getElementById('filter').style.width = `${Math.max(e.currentTarget.offsetWidth, 250)}px`
    !isOpen ? setAnchorEl(e.currentTarget) : setAnchorEl(null)
    setIsOpen(!isOpen)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsOpen(false)
  }

  const handleChange = key => {
    let index = -1
    const tempFilters = clone(innerFilters)
    for (let i = 0; i < tempFilters.length; i++) {
      if (tempFilters[i] === key) index = i
    }
    if (index > -1) {
      tempFilters.splice(index, 1)
      setInnerFilters(tempFilters)
      onChange(tempFilters)
    } else {
      let newFilter
      for (let i = 0; i < options.length; i++) {
        if (options[i] === key) newFilter = options[i]
      }
      if (newFilter) {
        tempFilters.push(newFilter)
        setInnerFilters(tempFilters)
        onChange(tempFilters)
      }
    }
  }

  const isChecked = key => {
    for (let i = 0; i < innerFilters.length; i++) {
      if (innerFilters[i] === key) return true
    }
    return false
  }

  return (
    <div>
      <div aria-controls="filter" aria-haspopup="true" onMouseUp={handleClick}>
        <L display="grid" flow="column" gap={0.5} ai="center" selectable>
          <Type variant="subtitle" color="secondary">
            <span
              dangerouslySetInnerHTML={{
                __html: `Filter${
                  filters.length
                    ? `s: <span style="font-weight: 600;">${
                        filters.length > 2 ? filters.length : filters.map(key => FILTER_OPTIONS[key]).join(', ')
                      }</span>`
                    : ''
                }`,
              }}
            />
          </Type>
          <IconFilter />
        </L>
      </div>
      <StyledMenu
        id="filter"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ width: `${dropdownWidth}px` }}
      >
        {options.map(option => (
          <StyledMenuItem
            key={option}
            className="menu-item--lg menu-item--primary"
            onMouseUp={() => {
              if (availableOptions.includes(option)) handleChange(option)
            }}
          >
            <StyledMenuItemInner isDisabled={!availableOptions.includes(option)}>
              <Checkbox size="lg" checked={isChecked(option)} onChange={e => e.stopPropagation()} />
              <Type variant="subtitle" color="secondary">
                {FILTER_OPTIONS[option]}
              </Type>
            </StyledMenuItemInner>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </div>
  )
}

const CollectionsPage = ({ location, classes, match }) => {
  const firestore = useSelector(({ firestore }) => firestore)
  const { data, ordered } = firestore
  const collections = ordered?.[COLLECTIONS_COLLECTION] || []
  const simulations = data?.[SIMULATIONS_COLLECTION] || {}
  const config = data?.[WEBSITE_CONFIG_COLLECTION]?.[WEBSITE_CONFIG_ID]
  const firebase = useSelector(({ firebase }) => firebase)
  const { auth, profile } = firebase
  const account = useSelector(({ account }) => account)
  const { claims, searchItems } = account
  const dispatch = useDispatch()
  const [tabs, setTabs] = useState([{ link: '/', label: 'Dashboard' }])

  const setSearch = newSearchItems => dispatch(AccountActions.AccountSetSearch(newSearchItems))

  const [filters, setFilters] = useState([])

  const getFilterOptions = () => {
    let filterOptions = []
    if (location.hash) {
      for (let i = 0; i < collections.length; i++) {
        if (`#${collections[i].id}` === location.hash) {
          for (let j = 0; j < collections[i].simulations.length; j++) {
            const sim = simulations[collections[i].simulations[j]] || {}
            if (sim.hasOwnProperty('filters')) filterOptions = [...filterOptions, ...sim.filters]
          }
        }
      }
    } else {
      for (const i in simulations) {
        if (simulations[i].hasOwnProperty('filters')) filterOptions = [...filterOptions, ...simulations[i].filters]
      }
    }
    return uniq(filterOptions)
  }

  const handleFilterChange = newFilters => setFilters(newFilters)

  const renderSimulations = collection => {
    return collection.simulations.map(simulationKey => {
      const simulation = simulations[simulationKey]
      if (!simulation) return
      const key = `${collection.id}_${simulation.id}_${filters.join('')}`
      let showSim = PRODUCTION ? !simulation.hasOwnProperty('hideProd') : true
      if (showSim) showSim = filters.length ? !!intersection(filters, simulation.filters).length : true

      return (
        showSim && (
          <SimulationBanner
            id={simulation.id}
            key={key}
            clickId={key}
            simulation={simulation}
            collection={collection}
            activeFilters={filters}
          />
        )
      )
    })
  }

  const renderCollections = (collectionsToRender, filterOptions) => {
    let filter
    switch (match.path) {
      case '/verticals':
        filter = 'vertical'
        break
      case '/events':
        filter = 'event'
        break
      default:
        filter = ''
    }
    return Object.keys(collectionsToRender).map(collectionKey => {
      const col = collectionsToRender[collectionKey]
      if (col.type === filter) {
        let hasSimulations = false
        const viewAll = location.hash ? location.hash === `#${col.id}` : true
        for (let j = 0; j < col.simulations.length; j++) {
          const sim = simulations[col.simulations[j]]
          if (sim && (PRODUCTION ? !sim.hasOwnProperty('hideProd') : true)) {
            if (filters.length && !!intersection(filters, sim.filters).length) hasSimulations = true
            if (!filters.length) hasSimulations = true
          }
        }

        return (
          hasSimulations &&
          viewAll && (
            <div key={collectionKey}>
              <L display="grid" flow="column" gap={2} jc="space-between" ai="center" mB={3}>
                <ScrollIntoView selector={`#${col.id}`} alignToTop>
                  <div className={classes.collectionRowHead}>
                    <Type variant={location.hash ? 'titleLarge' : 'titleMedium'} className={classes.collectionRowTitle}>
                      {col.name}
                      <div
                        id={col.id}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: -60,
                        }}
                      />
                    </Type>
                  </div>
                </ScrollIntoView>
                {!!location.hash && (
                  <Filter
                    filters={filters}
                    options={Object.keys(FILTER_OPTIONS)}
                    availableOptions={filterOptions}
                    onChange={handleFilterChange}
                  />
                )}
              </L>
              <SimulationGrid>{renderSimulations(col)}</SimulationGrid>
            </div>
          )
        )
      }
    })
  }

  const createTabs = () => {
    const filteredArr = []
    if (collections) {
      Object.keys(collections).map(collectionKey => {
        const { type } = collections[collectionKey]
        if (!filteredArr.includes(type)) filteredArr.push(type)
      })
    }
    const tabsArr = [{ link: '/', label: 'Dashboard' }]
    if (filteredArr.includes('vertical')) {
      tabsArr.push({ link: '/verticals', label: 'Verticals' })
    }
    if (filteredArr.includes('event')) {
      tabsArr.push({ link: '/events', label: 'Events' })
    }

    setTabs(tabsArr)
  }

  const debouncedCreateTabs = debounce(createTabs, 300)

  useEffect(() => {
    document.title = 'IdeaCloud | Collections'
    return () => {
      setSearch([])
    }
  }, [])

  useEffect(() => {
    handleFilterChange([])
  }, [match])

  useEffect(() => {
    debouncedCreateTabs()
  }, [collections])

  if (!auth.isLoaded || !profile.isLoaded || claims === null || !config) return null

  if (auth.isEmpty) return <LoginSSO />

  const filterOptions = getFilterOptions()

  const title = location.pathname.includes('/verticals')
    ? 'Verticals'
    : location.pathname.includes('/events')
    ? 'Events'
    : ''

  return (
    <Layout title="Home" hide full backgroundColor="#F9F9FB" tabs={tabs} tab={match.path}>
      <L container pT={3}>
        <L display="inline-grid" columns="max-content" mB={2}>
          <Link to="/">
            <L display="grid" flow="column" ai="center" jc="flex-start" selectable color="secondaryHueLightish">
              <IconArrowLeftDark />
              <Type variant="subtitle">Back</Type>
            </L>
          </Link>
        </L>
        {!location.hash && (
          <L display="grid" ai="center" gap={3} columns="1fr auto">
            {!searchItems.length && (
              <Type variant="titleLarge" mB={3}>
                {title}
              </Type>
            )}
            {!location.hash && (
              <Filter
                filters={filters}
                availableOptions={filterOptions}
                options={Object.keys(FILTER_OPTIONS)}
                onChange={handleFilterChange}
              />
            )}
            {!!searchItems.length && <Sort />}
          </L>
        )}
      </L>
      <L bgColor="#F9F9FB" grow position="relative">
        <ShadowBox active={!!searchItems.length} />
        <L container>
          <L display="grid" gap={6} mB={16}>
            {renderCollections(collections, filterOptions)}
          </L>
        </L>
      </L>
    </Layout>
  )
}

export default withStyles(CollectionsStyles)(CollectionsPage)
